import React from 'react';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { LabsmartsGuage } from '../charts/LabsmartsGauge';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

const arrowUpStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px'
};

const arrowDownStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px'
};

const conditionTitleStyle: React.CSSProperties = {
  color: 'black',
  textAlign: 'left'
};

const conditionListStyle: React.CSSProperties = {
  paddingLeft: 0,
  marginLeft: 0,
  listStyle: 'none'
};

const bulletStyle: React.CSSProperties = {
  display: 'inline-block',
  marginRight: '3px'
};

const markerStyle: React.CSSProperties = {
  color: 'black',
  textAlign: 'right'
};

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
};

export class GlucoseRegulationInsights extends React.Component<Props, {}> {

  renderArrow(direction, marker) {
    return (
      <>
        <img
          src={direction === 'up' ? arrow_up : arrow_down}
          alt={direction === 'up' ? '↑' : '↓'}
          style={direction === 'up' ? arrowUpStyle : arrowDownStyle}
        />
        {marker}
      </>
    );
  }

  markerIsHigh = (marker) => {
    return marker?.isGreaterThanLab?.() || marker?.isGreaterThanOptimal?.();
  };

  markerIsLow = (marker) => {
    return marker?.isLessThanLab?.() || marker?.isLessThanOptimal?.();
  };

  markerIsOptimal = (marker) => {
    return marker?.hasValue?.() && !this.markerIsHigh(marker) && !this.markerIsLow(marker);
  };

  renderPatternBox = (title, bullets, markers) => {
    return (
      <div className="condition-section">
        <div style={conditionTitleStyle}>
          <div>{title} Consistent with:</div>
        </div>
        <div style={{ textAlign: 'left' }}>
          <ul style={conditionListStyle}>
            {bullets.map((bullet) => (
              <li key={bullet} style={{ display: 'flex', alignItems: 'flex-start' }}>
                <span style={bulletStyle}>•</span>
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
        </div>
        <div style={markerStyle}>
          {markers.map((marker, index) => (
            <React.Fragment key={`${marker.direction}-${marker.label}-${index}`}>
              {index > 0 && <>,{' '}</>}
              {this.renderArrow(marker.direction, marker.label)}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // ─── Shared inner decision tree ──────────────────────────────────────────────
  // Returns { bullets, markers } for the most-specific insulin/cpeptide pattern.
  // contextLabel: e.g. "Limited Long-Term Glycemic Context"
  // lipidBullet: e.g. "Associated Lipid Dysregulation" or null
  // primaryMarkers: base markers (e.g. [{ direction: 'up', label: 'Glucose' }])
  // lipidMarker: extra lipid marker or null

  buildInsulinPattern(ba, contextLabel, lipidBullet, primaryMarkers, lipidMarker) {
    const insulinHigh = this.markerIsHigh(ba.insulin);
    const insulinLow  = this.markerIsLow(ba.insulin);
    const insulinOpt  = this.markerIsOptimal(ba.insulin);
    const cpHigh      = this.markerIsHigh(ba.cpeptide);
    const cpLow       = this.markerIsLow(ba.cpeptide);
    const cpOpt       = this.markerIsOptimal(ba.cpeptide);

    const buildBullets = (title) =>
      [title, ...(lipidBullet ? [lipidBullet] : []), contextLabel];

    const buildMarkers = (extra: any[]) =>
      [...primaryMarkers, ...(lipidMarker ? [lipidMarker] : []), ...extra];

    // Most specific first (both high)
    if (insulinHigh && cpHigh) {
      return {
        bullets: buildBullets('Early Insulin Resistance'),
        markers: buildMarkers([{ direction: 'up', label: 'Insulin' }, { direction: 'up', label: 'C-peptide' }])
      };
    }
    if (insulinHigh) {
      return {
        bullets: buildBullets('Early Insulin Resistance'),
        markers: buildMarkers([{ direction: 'up', label: 'Insulin' }])
      };
    }
    if (cpHigh) {
      return {
        bullets: buildBullets('Early Insulin Resistance'),
        markers: buildMarkers([{ direction: 'up', label: 'C-peptide' }])
      };
    }
    if (insulinOpt && cpOpt) {
      return {
        bullets: buildBullets('Impaired Glucose Regulation'),
        markers: buildMarkers([])
      };
    }
    if (insulinOpt) {
      return {
        bullets: buildBullets('Impaired Glucose Regulation'),
        markers: buildMarkers([])
      };
    }
    if (cpOpt) {
      return {
        bullets: buildBullets('Impaired Glucose Regulation'),
        markers: buildMarkers([])
      };
    }
    if (insulinLow && cpLow) {
      return {
        bullets: buildBullets('Impaired Glucose Regulation with Low Insulin Availability'),
        markers: buildMarkers([{ direction: 'down', label: 'Insulin' }, { direction: 'down', label: 'C-peptide' }])
      };
    }
    if (insulinLow) {
      return {
        bullets: buildBullets('Impaired Glucose Regulation with Low Insulin Availability'),
        markers: buildMarkers([{ direction: 'down', label: 'Insulin' }])
      };
    }
    if (cpLow) {
      return {
        bullets: buildBullets('Impaired Glucose Regulation with Low Insulin Availability'),
        markers: buildMarkers([{ direction: 'down', label: 'C-peptide' }])
      };
    }
    // No insulin/cpeptide value
    return {
      bullets: buildBullets('Impaired Glucose Regulation'),
      markers: buildMarkers([])
    };
  }

  // Determine the lipid bullet and lipid marker for a given ba
  getLipidContext(ba): { lipidBullet: string | null; lipidMarker: any | null } {
    if (this.markerIsHigh(ba.triglyceridesHDLRatio)) {
      return {
        lipidBullet: 'Associated Lipid Dysregulation',
        lipidMarker: { direction: 'up', label: 'TG/HDL' }
      };
    }
    if (this.markerIsHigh(ba.triglycerides) && (this.markerIsLow(ba.hdl) || this.markerIsOptimal(ba.hdl) === false)) {
      return {
        lipidBullet: 'Associated Lipid Dysregulation',
        lipidMarker: { direction: 'up', label: 'TG' }
      };
    }
    return { lipidBullet: null, lipidMarker: null };
  }

  // ─── Primary scenario: High Glucose, No HbA1c (patterns 1.x, 2.x, 3.x) ─────

  renderHighGlucoseNoHbA1c = (ba) => {
    if (!ba.glucose?.hasValue?.()) return null;
    if (ba.hemoglobinA1c?.hasValue?.()) return null;
    if (!this.markerIsHigh(ba.glucose)) return null;

    const { lipidBullet, lipidMarker } = this.getLipidContext(ba);
    const pattern = this.buildInsulinPattern(
      ba,
      'Limited Long-Term Glycemic Context',
      lipidBullet,
      [{ direction: 'up', label: 'Glucose' }],
      lipidMarker
    );
    return this.renderPatternBox('Metabolic Imbalance', pattern.bullets, pattern.markers);
  };

  // ─── Primary scenario: High HbA1c, No Glucose (patterns 4.x, 5.x, 6.x) ─────

  renderHighHbA1cNoGlucose = (ba) => {
    if (!ba.hemoglobinA1c?.hasValue?.()) return null;
    if (ba.glucose?.hasValue?.()) return null;
    if (!this.markerIsHigh(ba.hemoglobinA1c)) return null;

    const insulinHigh = this.markerIsHigh(ba.insulin);
    const insulinLow  = this.markerIsLow(ba.insulin);
    const insulinOpt  = this.markerIsOptimal(ba.insulin);
    const cpHigh      = this.markerIsHigh(ba.cpeptide);
    const cpLow       = this.markerIsLow(ba.cpeptide);
    const cpOpt       = this.markerIsOptimal(ba.cpeptide);

    const { lipidBullet, lipidMarker } = this.getLipidContext(ba);
    const base = [{ direction: 'up', label: 'HbA1c' }];
    const withLipid = lipidMarker ? [...base, lipidMarker] : base;

    const buildBullets = (title) =>
      [title, ...(lipidBullet ? [lipidBullet] : []), 'Limited Fasting Glucose Context'];

    if (insulinHigh && cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance'), [...withLipid, { direction: 'up', label: 'Insulin' }, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance'), [...withLipid, { direction: 'up', label: 'Insulin' }]);
    }
    if (cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance'), [...withLipid, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinOpt && cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation'), withLipid);
    }
    if (insulinOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation'), withLipid);
    }
    if (cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation'), withLipid);
    }
    if (insulinLow && cpLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation with Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'Insulin' }, { direction: 'down', label: 'C-peptide' }]);
    }
    if (insulinLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation with Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'Insulin' }]);
    }
    if (cpLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation with Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'C-peptide' }]);
    }
    return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation'), withLipid);
  };

  // ─── Primary scenario: High Glucose, HbA1c ≤ optimal (patterns 7.x, 8.x, 9.x) ─

  renderHighGlucoseLowHbA1c = (ba) => {
    if (!ba.glucose?.hasValue?.()) return null;
    if (!ba.hemoglobinA1c?.hasValue?.()) return null;
    if (!this.markerIsHigh(ba.glucose)) return null;
    if (this.markerIsHigh(ba.hemoglobinA1c)) return null; // handled by high+high scenario

    const insulinHigh = this.markerIsHigh(ba.insulin);
    const insulinLow  = this.markerIsLow(ba.insulin);
    const insulinOpt  = this.markerIsOptimal(ba.insulin);
    const cpHigh      = this.markerIsHigh(ba.cpeptide);
    const cpLow       = this.markerIsLow(ba.cpeptide);
    const cpOpt       = this.markerIsOptimal(ba.cpeptide);

    const { lipidBullet, lipidMarker } = this.getLipidContext(ba);
    const hbA1cActuallyLow = this.markerIsLow(ba.hemoglobinA1c);
    const base = [
      { direction: 'up', label: 'Glucose' },
      ...(hbA1cActuallyLow ? [{ direction: 'down', label: 'HbA1c' }] : [])
    ];
    const withLipid = lipidMarker ? [...base, lipidMarker] : base;

    const buildBullets = (title) =>
      [title, ...(lipidBullet ? [lipidBullet] : []), 'Preserved Long-Term Glucose Control'];

    if (insulinHigh && cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Early Insulin Resistance'), [...withLipid, { direction: 'up', label: 'Insulin' }, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Early Insulin Resistance'), [...withLipid, { direction: 'up', label: 'Insulin' }]);
    }
    if (cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Early Insulin Resistance'), [...withLipid, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinOpt && cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Short-Term Glucose Regulation'), withLipid);
    }
    if (insulinOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Short-Term Glucose Regulation'), withLipid);
    }
    if (cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Short-Term Glucose Regulation'), withLipid);
    }
    if (insulinLow && cpLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation With Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'Insulin' }, { direction: 'down', label: 'C-peptide' }]);
    }
    if (insulinLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation With Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'Insulin' }]);
    }
    if (cpLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation With Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'C-peptide' }]);
    }
    return this.renderPatternBox('Metabolic Imbalance', buildBullets('Early Fasting Glucose Elevation'), withLipid);
  };

  // ─── Primary scenario: HbA1c High, Glucose ≤ optimal (patterns 10.x, 11.x, 12.x) ─

  renderLowGlucoseHighHbA1c = (ba) => {
    if (!ba.hemoglobinA1c?.hasValue?.()) return null;
    if (!ba.glucose?.hasValue?.()) return null;
    if (!this.markerIsHigh(ba.hemoglobinA1c)) return null;
    if (this.markerIsHigh(ba.glucose)) return null; // handled by high+high scenario

    const insulinHigh = this.markerIsHigh(ba.insulin);
    const insulinLow  = this.markerIsLow(ba.insulin);
    const insulinOpt  = this.markerIsOptimal(ba.insulin);
    const cpHigh      = this.markerIsHigh(ba.cpeptide);
    const cpLow       = this.markerIsLow(ba.cpeptide);
    const cpOpt       = this.markerIsOptimal(ba.cpeptide);

    const { lipidBullet, lipidMarker } = this.getLipidContext(ba);
    const glucoseActuallyLow = this.markerIsLow(ba.glucose);
    const base = [
      ...(glucoseActuallyLow ? [{ direction: 'down', label: 'Glucose' }] : []),
      { direction: 'up', label: 'HbA1c' }
    ];
    const withLipid = lipidMarker ? [...base, lipidMarker] : base;

    const buildBullets = (title) =>
      [title, ...(lipidBullet ? [lipidBullet] : []), 'Elevated Long-Term Glucose Levels'];

    if (insulinHigh && cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance'), [...withLipid, { direction: 'up', label: 'Insulin' }, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance'), [...withLipid, { direction: 'up', label: 'Insulin' }]);
    }
    if (cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance'), [...withLipid, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinOpt && cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation'), withLipid);
    }
    if (insulinOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation'), withLipid);
    }
    if (cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation'), withLipid);
    }
    if (insulinLow && cpLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation with Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'Insulin' }, { direction: 'down', label: 'C-peptide' }]);
    }
    if (insulinLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation with Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'Insulin' }]);
    }
    if (cpLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation with Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'C-peptide' }]);
    }
    return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation'), withLipid);
  };

  // ─── Primary scenario: High Glucose + High HbA1c (patterns 13.x, 14.x, 15.x) ─

  renderHighGlucoseHighHbA1c = (ba) => {
    if (!ba.glucose?.hasValue?.()) return null;
    if (!ba.hemoglobinA1c?.hasValue?.()) return null;
    if (!this.markerIsHigh(ba.glucose)) return null;
    if (!this.markerIsHigh(ba.hemoglobinA1c)) return null;

    const insulinHigh = this.markerIsHigh(ba.insulin);
    const insulinLow  = this.markerIsLow(ba.insulin);
    const insulinOpt  = this.markerIsOptimal(ba.insulin);
    const cpHigh      = this.markerIsHigh(ba.cpeptide);
    const cpLow       = this.markerIsLow(ba.cpeptide);
    const cpOpt       = this.markerIsOptimal(ba.cpeptide);

    const { lipidBullet, lipidMarker } = this.getLipidContext(ba);
    const base = [{ direction: 'up', label: 'Glucose' }, { direction: 'up', label: 'HbA1c' }];
    const withLipid = lipidMarker ? [...base, lipidMarker] : base;

    const buildBullets = (title) =>
      [title, ...(lipidBullet ? [lipidBullet] : []), 'Elevated Current and Long-Term Glucose Levels'];

    if (insulinHigh && cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance with Elevated Insulin Output'), [...withLipid, { direction: 'up', label: 'Insulin' }, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance with Elevated Insulin Output'), [...withLipid, { direction: 'up', label: 'Insulin' }]);
    }
    if (cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance with Elevated Insulin Output'), [...withLipid, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinOpt && cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance with Inadequate Insulin Response'), withLipid);
    }
    if (insulinOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance with Inadequate Insulin Response'), withLipid);
    }
    if (cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Insulin Resistance with Inadequate Insulin Response'), withLipid);
    }
    if (insulinLow && cpLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation with Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'Insulin' }, { direction: 'down', label: 'C-peptide' }]);
    }
    if (insulinLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation with Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'Insulin' }]);
    }
    if (cpLow) {
      return this.renderPatternBox('Metabolic Imbalance', buildBullets('Impaired Glucose Regulation with Low Insulin Availability'), [...withLipid, { direction: 'down', label: 'C-peptide' }]);
    }
    return this.renderPatternBox('Metabolic Imbalance', buildBullets('Sustained Blood Sugar Dysregulation'), withLipid);
  };

  // ─── Primary scenario: Low Glucose, HbA1c ≤ opt or no value (patterns 19.x) ──

  renderLowGlucoseLowOrNoHbA1c = (ba) => {
    if (!ba.glucose?.hasValue?.()) return null;
    if (!this.markerIsLow(ba.glucose)) return null;
    if (this.markerIsHigh(ba.hemoglobinA1c)) return null; // handled by low gluc + high HbA1c

    const insulinHigh = this.markerIsHigh(ba.insulin);
    const insulinLow  = this.markerIsLow(ba.insulin);
    const insulinOpt  = this.markerIsOptimal(ba.insulin);
    const cpHigh      = this.markerIsHigh(ba.cpeptide);
    const cpLow       = this.markerIsLow(ba.cpeptide);
    const cpOpt       = this.markerIsOptimal(ba.cpeptide);

    if (insulinHigh && cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', ['Increased Pancreatic Output Contributing to Low Glucose'], [{ direction: 'down', label: 'Glucose' }, { direction: 'up', label: 'Insulin' }, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinHigh) {
      return this.renderPatternBox('Metabolic Imbalance', ['Insulin-Driven Low Glucose'], [{ direction: 'down', label: 'Glucose' }, { direction: 'up', label: 'Insulin' }]);
    }
    if (cpHigh) {
      return this.renderPatternBox('Metabolic Imbalance', ['Insulin-Driven Low Glucose'], [{ direction: 'down', label: 'Glucose' }, { direction: 'up', label: 'C-peptide' }]);
    }
    if (insulinOpt && cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', ['Reduced Pancreatic Output Relative to Glucose Levels'], [{ direction: 'down', label: 'Glucose' }]);
    }
    if (insulinOpt) {
      return this.renderPatternBox('Metabolic Imbalance', ['Reduced Insulin Activity'], [{ direction: 'down', label: 'Glucose' }]);
    }
    if (cpOpt) {
      return this.renderPatternBox('Metabolic Imbalance', ['Reduced Insulin Activity'], [{ direction: 'down', label: 'Glucose' }]);
    }
    if (insulinLow || cpLow) {
      const extraMarkers: any[] = [];
      if (insulinLow) extraMarkers.push({ direction: 'down', label: 'Insulin' });
      if (cpLow) extraMarkers.push({ direction: 'down', label: 'C-peptide' });
      return this.renderPatternBox('Metabolic Imbalance', ['Reduced Insulin Activity'], [{ direction: 'down', label: 'Glucose' }, ...extraMarkers]);
    }
    return this.renderPatternBox('Metabolic Imbalance', ['Low Glucose Availability', 'Under-Fueled State', 'Reactive Blood Sugar Trends'], [{ direction: 'down', label: 'Glucose' }]);
  };

  // ─── Primary scenario: Optimal Glucose + High Insulin/C-peptide (patterns 21.x) ─

  renderOptimalGlucoseHighInsulin = (ba) => {
    if (!ba.glucose?.hasValue?.()) return null;
    if (!this.markerIsOptimal(ba.glucose)) return null;
    if (this.markerIsHigh(ba.hemoglobinA1c)) return null;

    const insulinHigh = this.markerIsHigh(ba.insulin);
    const cpHigh      = this.markerIsHigh(ba.cpeptide);

    if (!insulinHigh && !cpHigh) return null;

    const { lipidBullet, lipidMarker } = this.getLipidContext(ba);
    const base: any[] = [];
    if (insulinHigh) base.push({ direction: 'up', label: 'Insulin' });
    if (cpHigh) base.push({ direction: 'up', label: 'C-peptide' });

    if (lipidBullet) {
      return this.renderPatternBox(
        'Metabolic Imbalance',
        ['Insulin Resistance with Elevated Insulin Output', 'Reduced Insulin Sensitivity', lipidBullet, 'Early Metabolic Imbalance'],
        lipidMarker ? [...base, lipidMarker] : base
      );
    }
    return this.renderPatternBox(
      'Metabolic Imbalance',
      ['Elevated Insulin Output', 'Preserved Glucose Regulation', 'Early Metabolic Imbalance'],
      base
    );
  };

  // ─── Add-on: Atherogenic Lipid Profile (16.x) ────────────────────────────────

  renderLipidAddon = (ba) => {
    const glucoseOrHbA1cHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!glucoseOrHbA1cHigh) return null;
    if (!this.markerIsHigh(ba.ldl)) return null;
    if (!this.markerIsHigh(ba.triglyceridesHDLRatio) && !(this.markerIsHigh(ba.triglycerides) && !this.markerIsHigh(ba.hdl))) return null;

    const markers: any[] = [];
    if (ba.triglyceridesHDLRatio?.hasValue?.() && this.markerIsHigh(ba.triglyceridesHDLRatio)) {
      markers.push({ direction: 'up', label: 'TG/HDL' });
    } else {
      markers.push({ direction: 'up', label: 'TG' });
    }
    markers.push({ direction: 'up', label: 'LDL' });

    return this.renderPatternBox(
      'Metabolic Imbalance',
      ['Atherogenic Lipid Profile', 'Altered Lipid Transport', 'Increased Cardiometabolic Risk'],
      markers
    );
  };

  // ─── Add-on: Hepatic Contribution (17.x) ─────────────────────────────────────

  renderLiverAddon = (ba) => {
    const glucoseOrHbA1cHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!glucoseOrHbA1cHigh) return null;
    if (!this.markerIsHigh(ba.alt)) return null;
    if (!this.markerIsHigh(ba.triglyceridesHDLRatio) && !(this.markerIsHigh(ba.triglycerides))) return null;

    const tgMarker = (ba.triglyceridesHDLRatio?.hasValue?.() && this.markerIsHigh(ba.triglyceridesHDLRatio))
      ? { direction: 'up', label: 'TG/HDL' }
      : { direction: 'up', label: 'TG' };

    return this.renderPatternBox(
      'Metabolic Imbalance',
      ['Hepatic Contribution to Insulin Resistance', 'Impaired Lipid and Glucose Regulation', 'Elevated Liver-Related Activity'],
      [tgMarker, { direction: 'up', label: 'ALT' }]
    );
  };

  // ─── Add-on: Adrenal/Protein/Pancreatic (18.x) ───────────────────────────────

  renderAdrenalAddon = (ba) => {
    const glucoseOrHbA1cHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!glucoseOrHbA1cHigh) return null;
    if (!this.markerIsLow(ba.carbonDioxide)) return null;

    const bunHigh = this.markerIsHigh(ba.bun);
    if (bunHigh) {
      return this.renderPatternBox(
        'Metabolic Imbalance',
        ['Protein-Driven Metabolic Activity', 'Increased Nitrogen Turnover', 'Acid Load from Protein Metabolism'],
        [{ direction: 'down', label: 'CO2' }, { direction: 'up', label: 'BUN' }]
      );
    }
    return this.renderPatternBox(
      'Metabolic Imbalance',
      ['Stress-Influenced Glucose Elevation', 'Increased Metabolic Stress', 'Stress Hormone Activity'],
      [{ direction: 'down', label: 'CO2' }]
    );
  };

  // ─── Add-on: Inadequate Pancreatic Response (18.3–18.6) ──────────────────────

  renderPancreaticAddon = (ba) => {
    const glucoseOrHbA1cHigh = this.markerIsHigh(ba.glucose) || this.markerIsHigh(ba.hemoglobinA1c);
    if (!glucoseOrHbA1cHigh) return null;

    const insulinLow = ba.insulin?.hasValue?.() && this.markerIsLow(ba.insulin);
    const cpLow = ba.cpeptide?.hasValue?.() && this.markerIsLow(ba.cpeptide);
    if (!insulinLow || !cpLow) return null;

    const kLow = this.markerIsLow(ba.potassium);
    const caLow = this.markerIsLow(ba.calcium);

    if (kLow && caLow) {
      return this.renderPatternBox(
        'Inadequate Pancreatic Response',
        ['Low Potassium and Calcium May Be Contributing'],
        [{ direction: 'down', label: 'Insulin' }, { direction: 'down', label: 'C-peptide' }, { direction: 'down', label: 'K' }, { direction: 'down', label: 'Ca' }]
      );
    }
    if (kLow) {
      return this.renderPatternBox(
        'Inadequate Pancreatic Response',
        ['Low Potassium May Be Contributing'],
        [{ direction: 'down', label: 'Insulin' }, { direction: 'down', label: 'C-peptide' }, { direction: 'down', label: 'K' }]
      );
    }
    if (caLow) {
      return this.renderPatternBox(
        'Inadequate Pancreatic Response',
        ['Low Calcium May Be Contributing'],
        [{ direction: 'down', label: 'Insulin' }, { direction: 'down', label: 'C-peptide' }, { direction: 'down', label: 'Ca' }]
      );
    }
    return this.renderPatternBox(
      'Inadequate Pancreatic Response',
      [],
      [{ direction: 'down', label: 'Insulin' }, { direction: 'down', label: 'C-peptide' }]
    );
  };

  // ─── Add-ons: Low Glucose – stress/protein/B6 (20.x) ─────────────────────────

  renderLowGlucoseProteinAddon = (ba) => {
    if (!ba.glucose?.hasValue?.()) return null;
    if (!this.markerIsLow(ba.glucose)) return null;
    if (!this.markerIsHigh(ba.bun)) return null;
    if (ba.creatinine?.hasValue?.() && this.markerIsHigh(ba.creatinine)) return null;

    return this.renderPatternBox(
      'Protein-Influenced Metabolic Pattern',
      ['Increased Reliance on Protein Metabolism due to Reduced Carbohydrate Availability'],
      [{ direction: 'down', label: 'Glucose' }, { direction: 'up', label: 'BUN' }]
    );
  };

  renderLowGlucoseCortisolAddon = (ba) => {
    if (!ba.glucose?.hasValue?.()) return null;
    if (!this.markerIsLow(ba.glucose)) return null;

    const cortisolLow = this.markerIsLow(ba.cortisolAM) || this.markerIsLow(ba.cortisolPM);
    if (!cortisolLow) return null;

    return this.renderPatternBox(
      'Low Cortisol-Influenced Glucose Pattern',
      ['Reduced Glucocorticoid Support for Glucose Regulation'],
      [{ direction: 'down', label: 'Glucose' }, { direction: 'down', label: 'Cortisol' }]
    );
  };

  renderLowGlucoseB6Addons = (ba) => {
    if (!ba.glucose?.hasValue?.()) return null;
    if (!this.markerIsLow(ba.glucose)) return null;

    const b6Low  = ba.vitaminB6?.hasValue?.() && this.markerIsLow(ba.vitaminB6);
    const altLow = this.markerIsLow(ba.alt);
    const astLow = this.markerIsLow(ba.ast);

    // Show most specific first
    if (b6Low) {
      return this.renderPatternBox(
        'Low Vitamin B6 May Be Contributing',
        ['Reduced Glucose Release'],
        [{ direction: 'down', label: 'Glucose' }, { direction: 'down', label: 'B6' }]
      );
    }
    if (altLow && astLow) {
      return this.renderPatternBox(
        'Low ALT and AST May Reflect Reduced Vitamin B6 Status',
        ['Contributing to Reduced Glucose Release'],
        [{ direction: 'down', label: 'Glucose' }, { direction: 'down', label: 'ALT' }, { direction: 'down', label: 'AST' }]
      );
    }
    if (altLow) {
      return this.renderPatternBox(
        'Low ALT May Reflect Reduced Vitamin B6 Status',
        ['Contributing to Reduced Glucose Release'],
        [{ direction: 'down', label: 'Glucose' }, { direction: 'down', label: 'ALT' }]
      );
    }
    if (astLow) {
      return this.renderPatternBox(
        'Low AST May Reflect Reduced Vitamin B6 Status',
        ['Contributing to Reduced Glucose Release'],
        [{ direction: 'down', label: 'Glucose' }, { direction: 'down', label: 'AST' }]
      );
    }
    return null;
  };

  openWindow = () => {
    window.open('https://labsmarts.com/testresultsanalysis/#glucose');
  };

  render() {
    const { bloodworkAnalysis } = this.props;
    if (!bloodworkAnalysis.bloodwork) return null;

    const ba = bloodworkAnalysis;
    const hasAnyKey = ba.glucose?.hasValue?.() || ba.hemoglobinA1c?.hasValue?.() || ba.triglyceridesHDLRatio?.hasValue?.();
    if (!hasAnyKey) return null;

    const prob = bloodworkAnalysis.glucoseAnalysis?.glucoseRegulationProbability;
    const cat  = bloodworkAnalysis.glucoseAnalysis?.glucoseRegulationProbabilityCategory;
    if (prob === undefined || prob < 0) return null;

    const panelTooltip = (
      <Tooltip id="glucose-insights-tooltip">
        Click this icon to learn more about the items in this panel.
      </Tooltip>
    );

    return (
      <Card className="evaluationPanel" id="glucose-regulation-insights">
        <Card.Header className="evaluationPanelHeading">
          Glucose Regulation Insights&nbsp;
          <OverlayTrigger overlay={panelTooltip}>
            <FontAwesomeIcon icon="external-link-alt" onClick={this.openWindow} />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            <LabsmartsGuage
              text="Glucose Dysregulation is"
              chartContainerId="glucoseRegulationGuage"
              bloodwork={bloodworkAnalysis.bloodwork}
              probability={prob}
              category={cat}
            />

            {this.renderHighGlucoseHighHbA1c(ba)}
            {this.renderHighGlucoseNoHbA1c(ba)}
            {this.renderHighHbA1cNoGlucose(ba)}
            {this.renderHighGlucoseLowHbA1c(ba)}
            {this.renderLowGlucoseHighHbA1c(ba)}
            {this.renderLowGlucoseLowOrNoHbA1c(ba)}
            {this.renderOptimalGlucoseHighInsulin(ba)}

            {this.renderLipidAddon(ba)}
            {this.renderLiverAddon(ba)}
            {this.renderAdrenalAddon(ba)}
            {this.renderPancreaticAddon(ba)}
            {this.renderLowGlucoseProteinAddon(ba)}
            {this.renderLowGlucoseCortisolAddon(ba)}
            {this.renderLowGlucoseB6Addons(ba)}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
